import { useEffect, useState } from 'react'
import { getToken, onMessage, isSupported } from 'firebase/messaging'
import { messaging } from '@/firebase/firebase'
import { useAuth } from '@/context/AuthContext'
import { db } from '@/firebase/firebase'
import { doc, getDoc } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'

export default function FCMDebug() {
  const { user } = useAuth()
  const [token, setToken] = useState(null)
  const [messages, setMessages] = useState([])
  const [enabled, setEnabled] = useState(false)
  const [support, setSupport] = useState(null)

  // Test notification pipeline state — fires one of each (in-app, push,
  // email, SMS) to the calling user via the sendTestNotification
  // callable. Intended for pre-launch end-to-end delivery validation.
  const [pipelineRunning, setPipelineRunning] = useState(false)
  const [pipelineResult, setPipelineResult] = useState(null)
  const [pipelineError, setPipelineError] = useState(null)
  const [pipelineTypes, setPipelineTypes] = useState({
    inApp: true, push: true, email: true, sms: true,
  })

  const runPipelineTest = async () => {
    setPipelineRunning(true)
    setPipelineResult(null)
    setPipelineError(null)
    try {
      const types = Object.entries(pipelineTypes).filter(([, v]) => v).map(([k]) => k)
      const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendTestNotification')
      const res = await callable({ types })
      setPipelineResult(res.data)
    } catch (e) {
      setPipelineError(e?.message || String(e))
    } finally {
      setPipelineRunning(false)
    }
  }

  useEffect(() => {
    const init = async () => {
      const supported = await isSupported()
      setSupport(supported)

      if (!supported || !user) return

      const userSnap = await getDoc(doc(db, 'users', user.uid))
      const prefs = userSnap.exists() ? userSnap.data().preferences || {} : {}

      if (prefs.pushEnabled === false) {
        console.warn('🔕 Push is disabled in user settings')
        setEnabled(false)
        return
      }

      setEnabled(true)

      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        console.warn('🚫 Notifications not granted')
        return
      }

      const t = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY
      })

      if (t) {
        console.log('📲 Token:', t)
        setToken(t)
      }

      onMessage(messaging, (payload) => {
        console.log('🔥 Received FCM:', payload)
        setMessages(prev => [...prev, payload])
      })
    }

    init()
  }, [user])

  return (
    <div className="p-6 space-y-6 max-w-xl mx-auto">
      <h1 className="text-xl font-bold">📡 FCM Debugger</h1>
      <p>Supported: <strong>{String(support)}</strong></p>
      <p>Push Enabled: <strong>{String(enabled)}</strong></p>
      <p>Token:</p>
      <pre className="text-xs whitespace-pre-wrap break-all bg-zinc-100 dark:bg-zinc-800 p-3 rounded">{token || 'None'}</pre>

      <h2 className="font-semibold mt-6">📥 Received Messages</h2>
      <div className="space-y-2">
        {messages.length === 0 && <p className="text-zinc-500 italic">No messages received yet...</p>}
        {messages.map((msg, i) => (
          <pre key={i} className="text-xs bg-zinc-100 dark:bg-zinc-800 p-2 rounded overflow-x-auto">
            {JSON.stringify(msg, null, 2)}
          </pre>
        ))}
      </div>

      {/* End-to-end pipeline test. Fires one of each (in-app, push,
          email, SMS) to the CALLING user themselves via the
          sendTestNotification callable. Lets a coach validate that
          every delivery channel works before launch — without needing
          to wait for a real registration / weather event / overdue
          payment to organically trigger one. */}
      <div className="mt-8 p-4 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10">
        <h2 className="font-bold text-[#0d1f14] mb-1">🧪 End-to-end pipeline test</h2>
        <p className="text-xs text-zinc-600 mb-3">
          Fires one of each notification type to YOU. Pre-launch sanity check.
        </p>
        <div className="flex flex-wrap gap-3 text-sm mb-3">
          {Object.entries(pipelineTypes).map(([k, v]) => (
            <label key={k} className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={v}
                onChange={(e) => setPipelineTypes((p) => ({ ...p, [k]: e.target.checked }))}
                disabled={pipelineRunning}
              />
              <span>{k}</span>
            </label>
          ))}
        </div>
        <button
          type="button"
          onClick={runPipelineTest}
          disabled={pipelineRunning || !Object.values(pipelineTypes).some(Boolean)}
          className="px-4 py-2 rounded-lg bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] text-sm font-bold uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {pipelineRunning ? 'Firing…' : 'Send test notifications to me'}
        </button>
        {pipelineError && (
          <pre className="mt-3 text-xs bg-red-50 text-red-700 p-2 rounded whitespace-pre-wrap break-all">
            {pipelineError}
          </pre>
        )}
        {pipelineResult && (
          <pre className="mt-3 text-xs bg-white p-2 rounded border border-zinc-200 whitespace-pre-wrap break-all">
            {JSON.stringify(pipelineResult, null, 2)}
          </pre>
        )}
      </div>
    </div>
  )
}